// C4.1 — Provider Health dashboard helpers (pure; unit-tested).
import type { ProviderHealthRollup } from "./api";

export type HealthTone = "good" | "warn" | "bad";

/**
 * Status classification for a provider rollup row. Mirrors the admin-bff
 * fallback thresholds so list cards and the dashboard agree:
 *   bad  — ≥50% of 24h events failed, or sync uptime below 50%
 *   warn — any failures, DLQ backlog, or integrations in error state
 *   good — everything else
 */
export function rollupTone(p: ProviderHealthRollup): HealthTone {
  if (p.errorRate24h >= 0.5 || (p.syncUptime24h !== null && p.syncUptime24h < 0.5)) return "bad";
  if (p.failures24h > 0 || p.dlqDepth > 0 || p.errorIntegrations > 0 || p.webhookFailures24h > 0) return "warn";
  return "good";
}

export function rollupLabel(tone: HealthTone): string {
  return tone === "good" ? "Healthy" : tone === "warn" ? "Attention" : "Degraded";
}

/** 0.0123 → "1.2%"; 0.997 → "99.7%"; 1 → "100%"; 0 → "0%". */
export function formatRate(rate: number): string {
  if (!Number.isFinite(rate) || rate <= 0) return "0%";
  const pct = Math.round(rate * 1000) / 10;
  return `${pct % 1 === 0 ? Math.round(pct) : pct}%`;
}

/** null → "—" (no runs in window); 1 → "100%". */
export function formatUptime(uptime: number | null): string {
  if (uptime === null || !Number.isFinite(uptime)) return "—";
  return formatRate(uptime);
}

/** Sort: degraded first, then attention, then healthy; ties by connected tenants desc. */
export function sortRollup(rows: ProviderHealthRollup[]): ProviderHealthRollup[] {
  const rank: Record<HealthTone, number> = { bad: 0, warn: 1, good: 2 };
  return [...rows].sort((a, b) => {
    const byTone = rank[rollupTone(a)] - rank[rollupTone(b)];
    if (byTone !== 0) return byTone;
    return b.connectedTenants - a.connectedTenants;
  });
}
